<template>
  <div class="drag-and-drop-area">
    <UploadPhotosNonAdventure
      class="drag-and-drop-area__upload-form"
      :customerId="props.customerId"
      @uploadingStarted="isUploadingStarted = true"
      @imageUploadedToServer="
        ({ serverId }) => {
          emit('update:imagesList', [...props.imagesList, serverId]);
        }
      "
      @uploadProgressChange="isUploadInProgress => (_isUploadInProgress = isUploadInProgress)"
      style="opacity: 0 !important"
    />

    <!-- "Upload 20 photos or more" note -->
    <div class="upload-20-photos-or-more-note drag-and-drop-area__upload-20-photos-or-more-note">
      <IconEmbedded class="upload-20-photos-or-more-note__icon" name="upload_1-5" :size="60" color="rgba(36, 61, 74, 0.5)" />

      <h3 v-if="!props.imagesList.length" class="global-h3 upload-20-photos-or-more-note__title">
        Upload {{ props.minImagesNumber }}+ photos or videos to get started<br />
        (the more the better)
      </h3>

      <h3 v-else class="global-h3 upload-20-photos-or-more-note__title">
        <b>{{ props.imagesList.length }}</b> added, click to upload more!
      </h3>
    </div>
    <!-- / "Upload 20 photos or more" note -->

    <div class="drag-and-drop-area__divider"></div>

    <AiPostGenerationVisualSchema
      v-if="!props.imagesList.length && !_isUploadInProgress"
      class="drag-and-drop-area__ai-post-gen-visual-schema"
      :isWithCtaButton="false"
      size="tiny"
      circlesColor="white"
      bgColor="pale-blue"
    />

    <template v-else>
      <!-- Thumbnails row -->
      <div class="thumbnails-row drag-and-drop-area__thumbnails-row">
        <ul class="thumbnails-row__row-wrap">
          <SrpFileThumbnail class="thumbnails-row__thumbnail" v-for="fileName in props.imagesList.slice(-10)" :key="fileName" :fileName="fileName" :isFixedAspectRatio="false" />

          <li v-if="_isUploadInProgress" class="thumbnails-row__thumbnail-placeholder">
            <LoadingIcon :size="30" />
          </li>
        </ul>
      </div>
      <!-- / Thumbnails row -->

      <!-- Upload progressbar -->
      <div v-if="props.imagesList.length <= props.minImagesNumber || _isUploadInProgress" class="upload-progressbar drag-and-drop-areaq__upload-progressbar">
        <div class="upload-progressbar__title">
          <b>{{ props.minImagesNumber - props.imagesList.length }}</b> more needed to generate post
        </div>
        <div class="upload-progressbar__bar-track">
          <div class="upload-progressbar__bar" :style="{ width: `${props.imagesList.length / (props.minImagesNumber / 100)}%` }"></div>
        </div>
      </div>
      <!-- / Upload progressbar -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

// Components
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import AiPostGenerationVisualSchema from "@components/AiPostGenerationVisualSchema.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import UploadPhotosNonAdventure from "@views/CMS/UploadPhotosNonAdventure.vue";

const props = withDefaults(
  defineProps<{
    customerId: string;
    minImagesNumber: number;
    imagesList: Array<string>;
  }>(),
  {
    customerId: "",
    minImagesNumber: 0,
    imagesList: () => [],
  }
);

const emit = defineEmits<{
  (e: "update:imagesList", value: Array<string>): void;
}>();

// Is Uploading started =======================================================
const isUploadingStarted = ref<boolean>(false);

// Is upload in progress ======================================================
const _isUploadInProgress = ref<boolean>(false);
</script>

<style scoped lang="scss">
// "Upload 20 photos or more" note ============================================
.upload-20-photos-or-more-note {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__icon {
    margin-bottom: 6px;
  }

  &__title {
    font-weight: 500;
    text-align: center;
  }
}

// Thumbnails row =============================================================
.thumbnails-row {
  width: calc(100% - 20px);
  max-width: 500px;
  height: 50px;
  list-style: none;
  display: flex;
  justify-content: flex-end;
  position: relative;
  overflow: hidden;

  -webkit-mask: linear-gradient(-90deg, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%);
  mask: linear-gradient(-90deg, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%);

  -webkit-mask-composite: destination-in;
  mask-composite: exclude;

  &__row-wrap {
    width: 2000px;
    min-width: 2000px;
    padding: 0;
    margin: 0;
    gap: 5px;
    display: flex;
    justify-content: flex-end;
  }

  &__thumbnail {
    height: 50px;
    display: inline-block;
  }

  &__thumbnail-placeholder {
    width: 60px;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.1);
  }
}

// Upload progressbar =========================================================
.upload-progressbar {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__title {
  }

  &__bar-track {
    width: 250px;
    max-width: calc(100% - 20px);
    height: 6px;
    border-radius: 100px;
    display: flex;
    justify-content: flex-start;
    background: rgba(203, 203, 203, 1);
  }

  &__bar {
    height: 6px;
    border-radius: 100px;
    background: rgba(5, 133, 135, 1);
  }
}

// Drag & Drop area ===========================================================
.drag-and-drop-area {
  min-height: 200px;
  padding: 35px 0 50px;
  border: 2px #a7acae dashed;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;
  overflow: hidden;
  background: #e7edf0;

  @mixin sectionTransparentForClicks {
    pointer-events: none;
    user-select: none;
  }

  &__upload-form {
    width: 100%;
    height: calc(100% + 30px);
    min-height: calc(100% + 30px);
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 3;
    opacity: 0;

    & > * {
      width: 100%;
      height: 100%;
    }
  }

  &__divider {
    @include sectionTransparentForClicks;
    width: calc(100% - 30px);
    max-width: 520px;
    margin-bottom: 25px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  // before upload starts -------------
  &__upload-20-photos-or-more-note {
    @include sectionTransparentForClicks;
    margin-bottom: 25px;
  }

  &__ai-post-gen-visual-schema {
    @include sectionTransparentForClicks;
  }

  // after upload starts --------------
  &__thumbnails-row {
    @include sectionTransparentForClicks;
    margin-bottom: 20px;
  }

  &__upload-progressbar {
    @include sectionTransparentForClicks;
  }
}
</style>
